/**
 * Credit Purchase Dialog Component
 * 
 * Displays available credit packs and handles credit purchases with Stripe payment.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { apiClient } from '../../lib/api/client';
import { StripePaymentDialog } from '../payment/StripePaymentDialog';
import { notifyWalletUpdate } from '../../lib/events/wallet-events';
import type { CreditPack } from '../../types';

interface CreditPurchaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseComplete?: () => void;
  recommendedPackId?: string | null;
}

interface PurchaseResponse {
  success: boolean;
  transaction_id?: string;
  purchase_id?: string;
  credit_amount?: number;
  new_balance?: number;
  message?: string;
  checkout_url?: string;
  session_id?: string;
  requires_payment?: boolean;
  error?: string;
}

export function CreditPurchaseDialog({ 
  open, 
  onOpenChange, 
  onPurchaseComplete,
  recommendedPackId,
}: CreditPurchaseDialogProps) {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Stripe payment state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);

  useEffect(() => {
    if (open) {
      loadPacks();
    } else {
      // Clear payment state when dialog closes
      setCheckoutUrl(null);
      setPurchaseId(null);
      setShowPaymentDialog(false);
      setSelectedPack(null);
    }
  }, [open]);

  const loadPacks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.credits.getPacks();
      setPacks(response.packs);
    } catch (err) {
      console.error('Error loading credit packs:', err);
      setError(t('credits.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packId: string) => {
    try {
      setPurchasing(true);
      setSelectedPackId(packId);
      setError(null);
      
      const pack = packs.find(p => p.pack_id === packId);
      setSelectedPack(pack || null);

      const result = await apiClient.credits.purchaseCredits(packId) as PurchaseResponse;

      if (result.success) {
        // Check if payment is required (checkout_url returned)
        if (result.checkout_url && result.purchase_id) {
          // Show Stripe payment dialog (opens in new tab)
          setCheckoutUrl(result.checkout_url);
          setPurchaseId(result.purchase_id);
          setShowPaymentDialog(true);
          setPurchasing(false);
          return;
        }
        
        // No payment required (dummy provider or already completed)
        // Notify header to refresh wallet balance
        notifyWalletUpdate(result.new_balance);
        onPurchaseComplete?.();
        onOpenChange(false);
      } else {
        setError(result.message || result.error || 'Purchase failed');
      }
    } catch (err: any) {
      console.error('Error purchasing credits:', err);
      setError(err.message || 'Failed to purchase credits');
    } finally {
      setPurchasing(false);
      setSelectedPackId(null);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setCheckoutUrl(null);
    setPurchaseId(null);
    setSelectedPack(null);
    
    // Notify header to refresh wallet balance
    notifyWalletUpdate();
    
    // Notify parent and close dialog
    onPurchaseComplete?.();
    onOpenChange(false);
  };

  const handlePaymentCancel = () => {
    setShowPaymentDialog(false);
    setCheckoutUrl(null);
    setPurchaseId(null);
    setSelectedPack(null);
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="dialog-content sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t('credits.purchaseCredits')}</DialogTitle>
            <DialogDescription>
              {t('credits.purchaseDescription')}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadPacks} variant="outline">
                {t('credits.tryAgain')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {packs.map((pack) => {
                const isRecommended = pack.pack_id === recommendedPackId;
                const isPurchasing = purchasing && selectedPackId === pack.pack_id;

                return (
                  <Card
                    key={pack.pack_id}
                    className={`credit-pack-card relative p-6 cursor-pointer transition-all hover:shadow-lg border-2 ${
                      isRecommended 
                        ? 'border-primary bg-primary/5' 
                        : pack.popular
                        ? 'border-primary/50'
                        : 'border-border'
                    }`}
                    onClick={() => !purchasing && handlePurchase(pack.pack_id)}
                  >
                    {pack.popular && (
                      <Badge className="absolute top-2 right-2 bg-primary">
                        {t('credits.mostPopular')}
                      </Badge>
                    )}
                    {isRecommended && (
                      <Badge className="absolute top-2 right-2 bg-green-600">
                        {t('credits.recommended')}
                      </Badge>
                    )}

                    <div className="flex flex-col items-center text-center">
                      {/* Pack name */}
                      <h3 className="font-medium text-muted-foreground mb-4">{pack.name}</h3>
                      
                      {/* Hero: Credit value (what user gets) */}
                      <div className="mb-2">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold tracking-tight">
                            ${pack.credit_amount_usd.toFixed(0)}
                          </span>
                          <span className="text-lg text-muted-foreground font-medium">
                            {t('credits.inCredits')}
                          </span>
                        </div>
                      </div>
                      
                      {/* Bonus highlight or Standard rate badge */}
                      <div className="mb-4">
                        {pack.bonus_percent > 0 ? (
                          <Badge className="bonus-badge bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100">
                            ${(pack.credit_amount_usd - pack.price_usd).toFixed(2)} {t('credits.free')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="standard-badge text-muted-foreground">
                            {t('credits.standardRate')}
                          </Badge>
                        )}
                      </div>

                      {/* Action button with price */}
                      <Button
                        className="buy-credits-btn w-full text-base py-5"
                        disabled={purchasing}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePurchase(pack.pack_id);
                        }}
                      >
                        {isPurchasing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('credits.processing')}
                          </>
                        ) : (
                          <>
                            <span className="font-normal">{t('credits.buyFor')}</span>{' '}
                            <span className="font-bold">{formatCurrency(pack.price_usd)}</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground text-center">
              {t('credits.creditsNeverExpire')}
              <br />
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stripe Payment Dialog - Opens checkout in new tab and polls for completion */}
      {checkoutUrl && purchaseId && showPaymentDialog && (
        <StripePaymentDialog
          open={showPaymentDialog}
          onOpenChange={(open) => {
            if (!open) handlePaymentCancel();
          }}
          checkoutUrl={checkoutUrl}
          purchaseId={purchaseId}
          amount={selectedPack?.price_usd}
          description={selectedPack ? `${selectedPack.name} (${selectedPack.credit_amount_usd.toFixed(2)} credits)` : undefined}
          title={t('credits.purchaseCredits')}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      )}
    </>
  );
}
